import React from 'react';

import { locale } from '@trqk/utils';

import { GradientProgressBar, IconFile } from '../..';

interface ProgressRowProps {
    /**
     * Row height
     */
    height: number;
    /**
     * Top / bottom margin to apply to the progress bar, to separate progress bars from each other
     */
    marginY: number;
    /**
     * An in-progress {@link UploadFileTask} or any task that extends an UploadFileTask
     */
    task: UploadFileTask;
    /**
     * Row width
     */
    width: number;
}

/**
 * A GradientProgressBar for an upload that has not yet failed or completed. Also shows an
 * in-progress label that is read from task.output.statusMessage, if it exists.
 * @param props - {@link ProgressRowProps}
 * @returns JSX
 */
export const ProgressRow = ({
    height,
    marginY,
    task,
    width,
}: ProgressRowProps): JSX.Element | null => {
    const { canceled, done, error, progress, input } = task;
    const output = task?.output ?? null;
    const filename = input?.filename ?? null;
    const currentStatusMessage =
        output?.statusMessage ??
        locale.enUS.COMPONENTS.ACTIVE_UPLOADS.DEFAULT_STATUS;
    const stopped = done === true || error !== null;

    // If the ingestion task is either failed or completed, do not render a GradientProgressBar
    if (stopped) {
        return null;
    }

    return (
        <div
            style={{
                marginTop: marginY,
                marginBottom: marginY,
            }}
        >
            <GradientProgressBar
                canceled={canceled}
                color="aqua"
                error={null}
                errorStyle="quiet"
                height={height}
                Icon={IconFile}
                inProgressLabel={`${currentStatusMessage} ...`}
                label={filename ?? '...'}
                labelLines={2}
                loadingSpinner={true}
                progress={progress}
                width={width}
            />
        </div>
    );
};
